import type {
  Component as ComponentStep,
  EntryPointConfig,
  EntryPointDcsDefaults,
  Option,
  Step,
} from '@de/types/types';
import type { FlowState } from '../../providers/dynamicExperienceReducer';
import type { DcsKey } from '@de/utils/taxonomy/fetchTaxonomy';

type WarnFn = (message: string) => void;

const ROOT_PATH = 'entryPoint';
const DCS_FIELDS = ['degree', 'category', 'subject'] as const;

/**
 * Pull `data.root.props.entryPoint` out of a Puck-shaped DSL payload.
 *
 * Mirrors {@link extractResultsPageConfig}: missing or non-object config
 * returns `undefined`, and malformed fields inside a present config are
 * dropped with warnings so a partially-bad block never breaks rendering.
 */
export function extractEntryPointConfig(
  data: unknown,
): EntryPointConfig | undefined {
  const props = getRootProps(data);
  if (!props || !('entryPoint' in props)) {
    return undefined;
  }

  const raw = props.entryPoint;
  if (!isPlainObject(raw)) {
    warn(`${ROOT_PATH} is not an object; skipping.`);
    return undefined;
  }

  return sanitizeEntryPointConfig(raw, warn);
}

/**
 * Resolve the entry step's `entryPoint` DSL config from a step list.
 *
 * Single extraction site for "find the entry step, pull its `entryPoint`
 * config" so DCS-default seeding and `forwardFields` threading can't drift
 * from each other (they previously duplicated this logic independently).
 */
export function getEntryPointConfigFromSteps(
  steps: Step[],
): EntryPointConfig | undefined {
  const entryStep = steps.find((step) => step.isEntry === true) ?? steps[0];
  const entryDslConfig = (
    entryStep as (Step & Partial<ComponentStep>) | undefined
  )?.dslConfig;
  return entryDslConfig?.type === 'entry'
    ? extractEntryPointConfig(entryDslConfig.data)
    : undefined;
}

function sanitizeEntryPointConfig(
  raw: Record<string, unknown>,
  warnFn: WarnFn,
): EntryPointConfig {
  const out: EntryPointConfig = {};

  for (const key of Object.keys(raw)) {
    if (
      key !== 'enabled' &&
      key !== 'defaults' &&
      key !== 'forwardFields' &&
      key !== 'subjectLabelFallback'
    ) {
      warnFn(`${ROOT_PATH}.${key} is not supported; skipping.`);
    }
  }

  if (raw.enabled !== undefined) {
    if (typeof raw.enabled === 'boolean') {
      out.enabled = raw.enabled;
    } else {
      warnFn(`${ROOT_PATH}.enabled is not a boolean; skipping.`);
    }
  }

  const defaults = sanitizeDcsDefaults(
    raw.defaults,
    `${ROOT_PATH}.defaults`,
    warnFn,
  );
  if (defaults) {
    out.defaults = defaults;
  }

  const forwardFields = sanitizeForwardFields(
    raw.forwardFields,
    `${ROOT_PATH}.forwardFields`,
    warnFn,
  );
  if (forwardFields !== undefined) {
    out.forwardFields = forwardFields;
  }

  const subjectLabelFallback = sanitizeSubjectLabelFallback(
    raw.subjectLabelFallback,
    `${ROOT_PATH}.subjectLabelFallback`,
    warnFn,
  );
  if (subjectLabelFallback !== undefined) {
    out.subjectLabelFallback = subjectLabelFallback;
  }

  return out;
}

/**
 * Keep a trim()-non-empty string. Non-strings and blank values are dropped
 * with warnings so a malformed fallback never invents heading copy.
 */
function sanitizeSubjectLabelFallback(
  raw: unknown,
  path: string,
  warnFn: WarnFn,
): string | undefined {
  if (raw === undefined) {
    return undefined;
  }
  if (typeof raw !== 'string') {
    warnFn(`${path} is not a string; skipping.`);
    return undefined;
  }
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    warnFn(`${path} is empty; skipping.`);
    return undefined;
  }
  return trimmed;
}

/**
 * Keep trim()-non-empty strings only. Non-array values and non-string /
 * blank entries are dropped with warnings. Surviving values are stored
 * trimmed so ctx param keys stay clean. An explicit empty array
 * round-trips as `[]` (distinct from omit = feature off).
 */
function sanitizeForwardFields(
  raw: unknown,
  path: string,
  warnFn: WarnFn,
): string[] | undefined {
  if (raw === undefined) {
    return undefined;
  }
  if (!Array.isArray(raw)) {
    warnFn(`${path} is not an array; skipping.`);
    return undefined;
  }

  const fields: string[] = [];
  for (let i = 0; i < raw.length; i++) {
    const value = raw[i];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        fields.push(trimmed);
        continue;
      }
    }
    warnFn(`${path}[${i}] is not a non-empty string; skipping.`);
  }
  return fields;
}

function sanitizeDcsDefaults(
  raw: unknown,
  path: string,
  warnFn: WarnFn,
): EntryPointDcsDefaults | undefined {
  if (raw === undefined) {
    return undefined;
  }
  if (!isPlainObject(raw)) {
    warnFn(`${path} is not an object; skipping.`);
    return undefined;
  }

  const defaults: EntryPointDcsDefaults = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!DCS_FIELDS.includes(key as (typeof DCS_FIELDS)[number])) {
      warnFn(`${path}.${key} is not supported; skipping.`);
      continue;
    }
    if (typeof value === 'string' && value.length > 0) {
      defaults[key as (typeof DCS_FIELDS)[number]] = value;
    } else {
      warnFn(`${path}.${key} is not a non-empty string; skipping.`);
    }
  }
  return defaults;
}

/**
 * Merge host-provided DCS context with an entry-point config's defaults.
 *
 * Rules:
 *   - When `config.enabled` is falsy (or no defaults), return `hostDcs`
 *     unchanged — the pre-config behaviour.
 *   - Otherwise, for each DCS dimension: keep the host value when present,
 *     else fall back to the configured default, else leave it unset.
 *
 * Host context always wins; defaults only fill gaps.
 *
 * This is the **seed-time** helper (host gaps at flow init). Blank
 * triad dims at advance/submit are filled by
 * {@link applyEntryPointDefaultsAtCommit}.
 */
export function applyEntryPointDcsDefaults(
  hostDcs: EntryPointDcsDefaults | undefined,
  config: EntryPointConfig | undefined,
): EntryPointDcsDefaults | undefined {
  if (!config?.enabled || !config.defaults) {
    return hostDcs;
  }

  const merged: EntryPointDcsDefaults = { ...(hostDcs ?? {}) };
  for (const field of DCS_FIELDS) {
    if (!merged[field] && config.defaults[field]) {
      merged[field] = config.defaults[field];
    }
  }
  return merged;
}

/**
 * Fill blank DCS triad options on the entry step from authored
 * `entryPoint.defaults` at advance/submit commit time.
 *
 * Seed-time {@link applyEntryPointDcsDefaults} only covers host gaps at
 * init. When a user later picks a real degree and leaves category/subject
 * blank (Select placeholders), branching still sees `''` unless defaults
 * are re-applied here — before `getNextStep` / eject URL / DCS product
 * reads. Non-blank user values are never overwritten. Returns the same
 * `flowState` reference when nothing changes.
 *
 * Intentionally does **not** touch DSM `formState` — defaults are
 * commit-time into flowState options only so Selects keep placeholders.
 */
export function applyEntryPointDefaultsAtCommit(
  flowState: FlowState,
  entryStepId: string | undefined,
  config: EntryPointConfig | undefined,
  translateDcsValue?: (key: DcsKey, slug: string) => string,
): FlowState {
  if (!entryStepId || !config?.enabled || !config.defaults) {
    return flowState;
  }

  const defaults = config.defaults;
  const hasAnyDefault = DCS_FIELDS.some((field) => {
    const value = defaults[field];
    return typeof value === 'string' && value.trim().length > 0;
  });
  if (!hasAnyDefault) {
    return flowState;
  }

  const stepData = flowState[entryStepId];
  const existingOptions = normalizeStepOptions(stepData?.option);
  let changed = false;
  const nextOptions = [...existingOptions];

  for (const field of DCS_FIELDS) {
    const defaultSlug = defaults[field];
    if (typeof defaultSlug !== 'string' || defaultSlug.trim().length === 0) {
      continue;
    }
    const slug = defaultSlug.trim();
    const label = translateDcsValue?.(field, slug) ?? slug;
    const index = nextOptions.findIndex(
      (opt) => opt.leadFieldAssociated === field,
    );

    if (index >= 0) {
      const current = nextOptions[index];
      if (!isBlankTriadOption(current)) {
        continue;
      }
      nextOptions[index] = {
        ...current,
        value: slug,
        slug,
        text:
          typeof current.text === 'string' && current.text.trim().length > 0
            ? current.text
            : label,
        leadFieldAssociated: field,
        optionId:
          typeof current.optionId === 'string' &&
          current.optionId.trim().length > 0
            ? current.optionId
            : field,
      };
      changed = true;
      continue;
    }

    nextOptions.push({
      optionId: field,
      text: label,
      value: slug,
      slug,
      leadFieldAssociated: field,
    });
    changed = true;
  }

  if (!changed) {
    return flowState;
  }

  const optionPayload: Option | Option[] =
    nextOptions.length === 1 ? nextOptions[0] : nextOptions;

  return {
    ...flowState,
    [entryStepId]: {
      ...(stepData ?? {}),
      option: optionPayload,
    },
  };
}

function normalizeStepOptions(option: unknown): Option[] {
  if (!option) {
    return [];
  }
  return Array.isArray(option) ? [...option] : [option as Option];
}

/**
 * A triad option is blank when it has no non-whitespace `value` and no
 * non-empty `slug` — the Select-placeholder shape after cascading clears.
 */
function isBlankTriadOption(option: Option): boolean {
  const valueBlank =
    option.value === undefined ||
    option.value === null ||
    (typeof option.value === 'string' && option.value.trim().length === 0);
  const slugBlank =
    typeof option.slug !== 'string' || option.slug.trim().length === 0;
  return valueBlank && slugBlank;
}

function getRootProps(data: unknown): Record<string, unknown> | undefined {
  if (!isPlainObject(data)) {
    return undefined;
  }
  const root = data.root;
  if (!isPlainObject(root)) {
    return undefined;
  }
  const props = root.props;
  if (!isPlainObject(props)) {
    return undefined;
  }
  return props;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function warn(message: string): void {
  // eslint-disable-next-line no-console
  console.warn(`[ConfigurableStep] ${message}`);
}
